/** Default illustration shared by page / table / modal empty states. */
export const EMPTY_STATE_IMAGE = 'assets/images/empty-state.png' as const;

/**
 * Illustration size (px) for {@link EmptyStateComponent} variants — from Figma
 * “Empty State sizes” (small / medium / big→large).
 */
export const EMPTY_STATE_IMAGE_SIZE_PX = {
  small: { width: 150, height: 100 },
  medium: { width: 186, height: 124 },
  large: { width: 300, height: 200 },
} as const;

export type EmptyStateVisualSize = keyof typeof EMPTY_STATE_IMAGE_SIZE_PX;

/** Fixed visual sizes plus `responsive` (follows viewport breakpoints). */
export type EmptyStateSize = EmptyStateVisualSize | 'responsive';

/**
 * Viewport floors for `size="responsive"`:
 * below 1440 → small, 1440–1920 → medium, above 1920 → large.
 */
export const EMPTY_STATE_BREAKPOINT_PX = {
  mediumMin: 1440,
  /** Exclusive — 1920px still maps to medium. */
  largeMin: 1920,
} as const;

export function emptyStateSizeFromWidth(width: number): EmptyStateVisualSize {
  if (width >= EMPTY_STATE_BREAKPOINT_PX.largeMin) {
    return 'large';
  }
  if (width >= EMPTY_STATE_BREAKPOINT_PX.mediumMin) {
    return 'medium';
  }
  return 'small';
}

/**
 * Tailwind classes per size — one place to see what each variant looks like.
 * Bound as whole class strings on the empty-state slots (no per-class toggles).
 *
 * Spacing/typography from Figma “Empty State sizes”:
 * - small:  title 16/24, desc 12, image→text 4, body→actions 16
 * - medium: title 20/30, desc 12, image→text 4, body→actions 16
 * - large:  title 20/30, desc 16/24, image→text 8, body→actions 24
 */
export const EMPTY_STATE_SIZE_CLASSES = {
  small: {
    root: 'gap-4',
    body: 'gap-1',
    icon: '!text-2xl',
    text: 'gap-2 max-w-[30rem]',
    title: 'text-base leading-6',
    description: 'text-xs leading-normal',
  },
  medium: {
    root: 'gap-4',
    body: 'gap-1',
    icon: '!text-4xl',
    text: 'gap-2 max-w-[30rem]',
    title: 'text-xl leading-[1.875rem]',
    description: 'text-xs leading-normal',
  },
  large: {
    root: 'gap-6',
    body: 'gap-2',
    icon: '!text-6xl',
    text: 'gap-2 max-w-[30rem]',
    title: 'text-xl leading-[1.875rem]',
    description: 'text-base leading-6',
  },
} as const satisfies Record<
  EmptyStateVisualSize,
  { root: string; body: string; icon: string; text: string; title: string; description: string }
>;
